import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Trophy, Award, Crown, Star, Medal, Target, BookOpen, Heart, MessageSquare, Share2, Download, Upload, Settings, Search, Grid, List, Zap, Flame, Bell, Gift, Sparkles, Rocket, Diamond, Gem, Coins, Banknote, CreditCard, Wallet, PiggyBank, ShoppingCart, ShoppingBag, Package, Truck, Home, Building, Square, Circle, Triangle, Hexagon, Octagon, Pentagon, Moon, Sun, Cloud, CloudRain, CloudSnow, CloudLightning, CloudDrizzle, CloudHail, CloudFog, CloudSun, CloudMoon, Sunrise, Sunset, StarHalf, StarOff, Sparkle } from 'lucide-react';

interface Achievement {
  id: string;
  title: string;
  description: string;
  icon: string;
  category: 'reading' | 'engagement' | 'social' | 'streak' | 'milestone' | 'special';
  rarity: 'common' | 'uncommon' | 'rare' | 'epic' | 'legendary';
  points: number;
  unlocked: boolean;
  unlockedAt?: Date;
  progress?: number;
  maxProgress?: number;
  requirements: string[];
  rewards: {
    points: number;
    badges?: string[];
    unlocks?: string[];
  };
}

interface UserStats {
  totalArticlesRead: number;
  totalReadingTime: number;
  currentStreak: number;
  longestStreak: number;
  averageReadingSpeed: number;
  comprehensionScore: number;
  socialEngagement: number;
  achievementsUnlocked: number;
  totalPoints: number;
}

interface AchievementSystemProps {
  userId: string;
  onAchievementUnlocked?: (achievement: Achievement) => void;
  onPointsEarned?: (points: number) => void;
}

const AchievementSystem: React.FC<AchievementSystemProps> = ({
  userId,
  onAchievementUnlocked,
  onPointsEarned
}) => {
  const [achievements, setAchievements] = useState<Achievement[]>([]);
  const [userStats, setUserStats] = useState<UserStats>({
    totalArticlesRead: 0,
    totalReadingTime: 0,
    currentStreak: 0,
    longestStreak: 0,
    averageReadingSpeed: 0,
    comprehensionScore: 0,
    socialEngagement: 0,
    achievementsUnlocked: 0,
    totalPoints: 0
  });
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [sortBy, setSortBy] = useState<'name' | 'rarity' | 'points' | 'progress'>('name');
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [showUnlockedOnly, setShowUnlockedOnly] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Sample achievements data
  const sampleAchievements: Achievement[] = [
    {
      id: 'first-read',
      title: 'First Steps',
      description: 'Read your first article',
      icon: 'BookOpen',
      category: 'milestone',
      rarity: 'common',
      points: 10,
      unlocked: true,
      unlockedAt: new Date('2024-01-01'),
      progress: 1,
      maxProgress: 1,
      requirements: ['Read 1 article'],
      rewards: { points: 10 }
    },
    {
      id: 'speed-reader',
      title: 'Speed Reader',
      description: 'Read 10 articles in one day',
      icon: 'Zap',
      category: 'reading',
      rarity: 'uncommon',
      points: 50,
      unlocked: false,
      progress: 7,
      maxProgress: 10,
      requirements: ['Read 10 articles in a single day'],
      rewards: { points: 50 }
    },
    {
      id: 'streak-master',
      title: 'Streak Master',
      description: 'Maintain a 30-day reading streak',
      icon: 'Flame',
      category: 'streak',
      rarity: 'rare',
      points: 200,
      unlocked: false,
      progress: 15,
      maxProgress: 30,
      requirements: ['Read articles for 30 consecutive days'],
      rewards: { points: 200, unlocks: ['Speed Reader Mode'] }
    },
    {
      id: 'social-butterfly',
      title: 'Social Butterfly',
      description: 'Share 25 articles with friends',
      icon: 'Share2',
      category: 'social',
      rarity: 'uncommon',
      points: 75,
      unlocked: false,
      progress: 12,
      maxProgress: 25,
      requirements: ['Share 25 articles'],
      rewards: { points: 75 }
    },
    {
      id: 'comprehension-expert',
      title: 'Comprehension Expert',
      description: 'Achieve 90% average comprehension score',
      icon: 'Target',
      category: 'reading',
      rarity: 'epic',
      points: 300,
      unlocked: false,
      progress: 85,
      maxProgress: 90,
      requirements: ['Maintain 90% comprehension for 10 articles'],
      rewards: { points: 300, unlocks: ['Advanced Analytics'] }
    },
    {
      id: 'legendary-reader',
      title: 'Legendary Reader',
      description: 'Read 1000 articles and maintain 95% comprehension',
      icon: 'Crown',
      category: 'milestone',
      rarity: 'legendary',
      points: 1000,
      unlocked: false,
      progress: 0,
      maxProgress: 1000,
      requirements: ['Read 1000 articles', 'Maintain 95% comprehension'],
      rewards: { points: 1000, unlocks: ['VIP Status', 'Exclusive Content'] }
    }
  ];

  useEffect(() => {
    loadAchievements();
    loadUserStats();
  }, [userId]);

  const loadAchievements = async () => {
    try {
      setIsLoading(true);
      // In a real app, this would fetch from your backend
      setAchievements(sampleAchievements);
    } catch (err) {
      setError('Failed to load achievements');
    } finally {
      setIsLoading(false);
    }
  };

  const loadUserStats = async () => {
    try {
      // In a real app, this would fetch from your backend
      setUserStats({
        totalArticlesRead: 47,
        totalReadingTime: 1250,
        currentStreak: 15,
        longestStreak: 23,
        averageReadingSpeed: 250,
        comprehensionScore: 87,
        socialEngagement: 12,
        achievementsUnlocked: 1,
        totalPoints: 10
      });
    } catch (err) {
      setError('Failed to load user stats');
    }
  };

  const checkAchievements = useCallback((action: string, data: any) => {
    const updatedAchievements = achievements.map(achievement => {
      if (achievement.unlocked) return achievement;

      let shouldUnlock = false;
      let newProgress = achievement.progress || 0;

      switch (achievement.id) {
        case 'first-read':
          if (action === 'article_read' && data.count >= 1) {
            shouldUnlock = true;
            newProgress = 1;
          }
          break;
        case 'speed-reader':
          if (action === 'daily_read' && data.count >= 10) {
            shouldUnlock = true;
            newProgress = 10;
          } else if (action === 'article_read') {
            newProgress = Math.min((achievement.progress || 0) + 1, 10);
          }
          break;
        case 'streak-master':
          if (action === 'streak_update' && data.streak >= 30) {
            shouldUnlock = true;
            newProgress = 30;
          } else if (action === 'streak_update') {
            newProgress = Math.min(data.streak, 30);
          }
          break;
        case 'social-butterfly':
          if (action === 'article_shared' && data.count >= 25) {
            shouldUnlock = true;
            newProgress = 25;
          } else if (action === 'article_shared') {
            newProgress = Math.min((achievement.progress || 0) + 1, 25);
          }
          break;
        case 'comprehension-expert':
          if (action === 'comprehension_update' && data.score >= 90) {
            shouldUnlock = true;
            newProgress = 90;
          } else if (action === 'comprehension_update') {
            newProgress = Math.min(data.score, 90);
          }
          break;
        case 'legendary-reader':
          if (action === 'article_read' && data.total >= 1000 && data.comprehension >= 95) {
            shouldUnlock = true;
            newProgress = 1000;
          } else if (action === 'article_read') {
            newProgress = Math.min(data.total, 1000);
          }
          break;
      }

      if (shouldUnlock) {
        const unlockedAchievement = {
          ...achievement,
          unlocked: true,
          unlockedAt: new Date(),
          progress: newProgress
        };
        
        onAchievementUnlocked?.(unlockedAchievement);
        onPointsEarned?.(achievement.points);
        
        return unlockedAchievement;
      }

      return {
        ...achievement,
        progress: newProgress
      };
    });

    setAchievements(updatedAchievements);
  }, [achievements, onAchievementUnlocked, onPointsEarned]);

  const filteredAchievements = achievements.filter(achievement => {
    const matchesCategory = selectedCategory === 'all' || achievement.category === selectedCategory;
    const matchesSearch = achievement.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         achievement.description.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesUnlocked = !showUnlockedOnly || achievement.unlocked;
    
    return matchesCategory && matchesSearch && matchesUnlocked;
  });

  const sortedAchievements = [...filteredAchievements].sort((a, b) => {
    switch (sortBy) {
      case 'name':
        return a.title.localeCompare(b.title);
      case 'rarity':
        const rarityOrder = { legendary: 5, epic: 4, rare: 3, uncommon: 2, common: 1 };
        return rarityOrder[b.rarity] - rarityOrder[a.rarity];
      case 'points':
        return b.points - a.points;
      case 'progress':
        const aProgress = (a.progress || 0) / (a.maxProgress || 1);
        const bProgress = (b.progress || 0) / (b.maxProgress || 1);
        return bProgress - aProgress;
      default:
        return 0;
    }
  });

  const getRarityColor = (rarity: string) => {
    switch (rarity) {
      case 'common': return 'text-gray-500';
      case 'uncommon': return 'text-green-500';
      case 'rare': return 'text-blue-500';
      case 'epic': return 'text-purple-500';
      case 'legendary': return 'text-yellow-500';
      default: return 'text-gray-500';
    }
  };

  const getRarityBgColor = (rarity: string) => {
    switch (rarity) {
      case 'common': return 'bg-gray-100 dark:bg-gray-800';
      case 'uncommon': return 'bg-green-100 dark:bg-green-900';
      case 'rare': return 'bg-blue-100 dark:bg-blue-900';
      case 'epic': return 'bg-purple-100 dark:bg-purple-900';
      case 'legendary': return 'bg-yellow-100 dark:bg-yellow-900';
      default: return 'bg-gray-100 dark:bg-gray-800';
    }
  };

  const getIconComponent = (iconName: string) => {
    const iconMap: { [key: string]: React.ComponentType<any> } = {
      BookOpen,
      Zap,
      Flame,
      Share2,
      Target,
      Crown,
      Trophy,
      Award,
      Medal,
      Star,
      Heart,
      MessageSquare,
      Download,
      Upload,
      Settings,
      Bell,
      Gift,
      Sparkles,
      Rocket,
      Diamond,
      Gem,
      Coins,
      Banknote,
      CreditCard,
      Wallet,
      PiggyBank,
      ShoppingCart,
      ShoppingBag,
      Package,
      Truck,
      Home,
      Building,
      Square,
      Circle,
      Triangle,
      Hexagon,
      Octagon,
      Pentagon,
      Moon,
      Sun,
      Cloud,
      CloudRain,
      CloudSnow,
      CloudLightning,
      CloudDrizzle,
      CloudHail,
      CloudFog,
      CloudSun,
      CloudMoon,
      Sunrise,
      Sunset,
      StarHalf,
      StarOff,
      Sparkle
    };
    
    return iconMap[iconName] || Trophy;
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center p-8">
        <div className="text-red-500 mb-4">{error}</div>
        <button 
          onClick={() => window.location.reload()}
          className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600"
        >
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto p-6">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
          Achievement System
        </h1>
        <p className="text-gray-600 dark:text-gray-400">
          Track your reading progress and unlock amazing rewards
        </p>
      </div>

      {/* Stats Overview */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <div className="bg-white dark:bg-gray-800 rounded-lg p-6 shadow-sm border border-gray-200 dark:border-gray-700">
          <div className="flex items-center">
            <div className="p-2 bg-blue-100 dark:bg-blue-900 rounded-lg">
              <Trophy className="h-6 w-6 text-blue-600 dark:text-blue-400" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Achievements</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">
                {userStats.achievementsUnlocked}/{achievements.length}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-lg p-6 shadow-sm border border-gray-200 dark:border-gray-700">
          <div className="flex items-center">
            <div className="p-2 bg-green-100 dark:bg-green-900 rounded-lg">
              <Star className="h-6 w-6 text-green-600 dark:text-green-400" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Total Points</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">
                {userStats.totalPoints.toLocaleString()}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-lg p-6 shadow-sm border border-gray-200 dark:border-gray-700">
          <div className="flex items-center">
            <div className="p-2 bg-orange-100 dark:bg-orange-900 rounded-lg">
              <Flame className="h-6 w-6 text-orange-600 dark:text-orange-400" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Current Streak</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">
                {userStats.currentStreak} days
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-lg p-6 shadow-sm border border-gray-200 dark:border-gray-700">
          <div className="flex items-center">
            <div className="p-2 bg-purple-100 dark:bg-purple-900 rounded-lg">
              <BookOpen className="h-6 w-6 text-purple-600 dark:text-purple-400" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Articles Read</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">
                {userStats.totalArticlesRead}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Filters and Controls */}
      <div className="bg-white dark:bg-gray-800 rounded-lg p-6 shadow-sm border border-gray-200 dark:border-gray-700 mb-6">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
              <input
                type="text"
                placeholder="Search achievements..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            <select
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e.target.value)}
              className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="all">All Categories</option>
              <option value="reading">Reading</option>
              <option value="engagement">Engagement</option>
              <option value="social">Social</option>
              <option value="streak">Streak</option>
              <option value="milestone">Milestone</option>
              <option value="special">Special</option>
            </select>

            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value as any)}
              className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="name">Sort by Name</option>
              <option value="rarity">Sort by Rarity</option>
              <option value="points">Sort by Points</option>
              <option value="progress">Sort by Progress</option>
            </select>
          </div>

          <div className="flex items-center gap-4">
            <label className="flex items-center">
              <input
                type="checkbox"
                checked={showUnlockedOnly}
                onChange={(e) => setShowUnlockedOnly(e.target.checked)}
                className="mr-2 rounded border-gray-300 dark:border-gray-600 text-blue-600 focus:ring-blue-500"
              />
              <span className="text-sm text-gray-700 dark:text-gray-300">Unlocked only</span>
            </label>

            <div className="flex border border-gray-300 dark:border-gray-600 rounded-lg">
              <button
                onClick={() => setViewMode('grid')}
                className={`p-2 ${viewMode === 'grid' ? 'bg-blue-500 text-white' : 'bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300'}`}
              >
                <Grid className="h-4 w-4" />
              </button>
              <button
                onClick={() => setViewMode('list')}
                className={`p-2 ${viewMode === 'list' ? 'bg-blue-500 text-white' : 'bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300'}`}
              >
                <List className="h-4 w-4" />
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Achievements Grid/List */}
      {viewMode === 'grid' ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {sortedAchievements.map((achievement) => {
            const IconComponent = getIconComponent(achievement.icon);
            const progressPercentage = achievement.maxProgress 
              ? ((achievement.progress || 0) / achievement.maxProgress) * 100 
              : 0;

            return (
              <div
                key={achievement.id}
                className={`bg-white dark:bg-gray-800 rounded-lg p-6 shadow-sm border border-gray-200 dark:border-gray-700 transition-all duration-200 hover:shadow-md ${
                  achievement.unlocked ? 'ring-2 ring-yellow-400' : ''
                }`}
              >
                <div className="flex items-start justify-between mb-4">
                  <div className={`p-3 rounded-lg ${getRarityBgColor(achievement.rarity)}`}>
                    <IconComponent className={`h-8 w-8 ${getRarityColor(achievement.rarity)}`} />
                  </div>
                  <div className="text-right">
                    <span className={`inline-block px-2 py-1 text-xs font-medium rounded-full ${getRarityBgColor(achievement.rarity)} ${getRarityColor(achievement.rarity)}`}>
                      {achievement.rarity}
                    </span>
                    <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                      {achievement.points} pts
                    </p>
                  </div>
                </div>

                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                  {achievement.title}
                </h3>
                <p className="text-gray-600 dark:text-gray-400 mb-4">
                  {achievement.description}
                </p>

                {achievement.maxProgress && achievement.maxProgress > 1 && (
                  <div className="mb-4">
                    <div className="flex justify-between text-sm text-gray-600 dark:text-gray-400 mb-1">
                      <span>Progress</span>
                      <span>{achievement.progress || 0}/{achievement.maxProgress}</span>
                    </div>
                    <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                      <div
                        className="bg-blue-500 h-2 rounded-full transition-all duration-300"
                        style={{ width: `${progressPercentage}%` }}
                      ></div>
                    </div>
                  </div>
                )}

                {achievement.unlocked && achievement.unlockedAt && (
                  <div className="text-sm text-green-600 dark:text-green-400 mb-2">
                    ✓ Unlocked {achievement.unlockedAt.toLocaleDateString()}
                  </div>
                )}

                <div className="text-xs text-gray-500 dark:text-gray-400">
                  <p className="mb-1">Requirements:</p>
                  <ul className="list-disc list-inside space-y-1">
                    {achievement.requirements.map((req, index) => (
                      <li key={index}>{req}</li>
                    ))}
                  </ul>
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <div className="space-y-4">
          {sortedAchievements.map((achievement) => {
            const IconComponent = getIconComponent(achievement.icon);
            const progressPercentage = achievement.maxProgress 
              ? ((achievement.progress || 0) / achievement.maxProgress) * 100 
              : 0;

            return (
              <div
                key={achievement.id}
                className={`bg-white dark:bg-gray-800 rounded-lg p-6 shadow-sm border border-gray-200 dark:border-gray-700 transition-all duration-200 hover:shadow-md ${
                  achievement.unlocked ? 'ring-2 ring-yellow-400' : ''
                }`}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-4">
                    <div className={`p-3 rounded-lg ${getRarityBgColor(achievement.rarity)}`}>
                      <IconComponent className={`h-8 w-8 ${getRarityColor(achievement.rarity)}`} />
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                        {achievement.title}
                      </h3>
                      <p className="text-gray-600 dark:text-gray-400">
                        {achievement.description}
                      </p>
                    </div>
                  </div>

                  <div className="text-right">
                    <span className={`inline-block px-2 py-1 text-xs font-medium rounded-full ${getRarityBgColor(achievement.rarity)} ${getRarityColor(achievement.rarity)} mb-2`}>
                      {achievement.rarity}
                    </span>
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      {achievement.points} pts
                    </p>
                    {achievement.unlocked && achievement.unlockedAt && (
                      <p className="text-xs text-green-600 dark:text-green-400 mt-1">
                        ✓ Unlocked {achievement.unlockedAt.toLocaleDateString()}
                      </p>
                    )}
                  </div>
                </div>

                {achievement.maxProgress && achievement.maxProgress > 1 && (
                  <div className="mt-4">
                    <div className="flex justify-between text-sm text-gray-600 dark:text-gray-400 mb-1">
                      <span>Progress</span>
                      <span>{achievement.progress || 0}/{achievement.maxProgress}</span>
                    </div>
                    <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                      <div
                        className="bg-blue-500 h-2 rounded-full transition-all duration-300"
                        style={{ width: `${progressPercentage}%` }}
                      ></div>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {sortedAchievements.length === 0 && (
        <div className="text-center py-12">
          <Trophy className="h-12 w-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
            No achievements found
          </h3>
          <p className="text-gray-600 dark:text-gray-400">
            Try adjusting your search criteria or filters
          </p>
        </div>
      )}
    </div>
  );
};

export default AchievementSystem;